/**
 * Application Entry Point
 *
 * Boot sequence: silent SSO session check first. With no session the
 * user is redirected to the corporate portal (the app has no login
 * form). React only mounts for authenticated users.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { initSso, redirectToPortal, getSsoToken } from './auth/sso';
import { syncAuthData } from './auth/authSync';
import './styles/index.css';

const boot = async (): Promise<void> => {
  const container = document.getElementById('root')!;

  // Layout-debug harness: dev server only, stripped from production builds.
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK === '1') {
    localStorage.setItem('token', 'mock');
    localStorage.setItem('role', 'superuser');
    localStorage.setItem('roles', JSON.stringify(['superuser']));
    localStorage.setItem('user', JSON.stringify({ id: 'mock', fullName: 'Mock User' }));
    localStorage.setItem('auth_method', 'sso');
    ReactDOM.createRoot(container).render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
    return;
  }

  try {
    const authenticated = await initSso();

    if (!authenticated) {
      redirectToPortal();
      return; // navigation in progress — render nothing to avoid a flash
    }

    const token = await getSsoToken();
    syncAuthData(token);

    ReactDOM.createRoot(container).render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  } catch {
    // SSO check failed (network, misconfiguration) — portal is the safe landing
    redirectToPortal();
  }
};

void boot();
